/**
 * t428u: the browser's side of "Set up your phone" (packages/shared devicePermissions has the rows, words and the
 * reminder rules; the iPhone app's version is apps/mobile/src/permissions).
 * - Notifications: Web Push (lib/push.ts): on means allowed and this browser subscribed. iPhone Safari only has push
 *   for a Home Screen app, so there it says how to add it.
 * - Microphone, camera, location: navigator.permissions where the browser can say; where it can't, the row says it's
 *   asked when needed (at the call, the QR scan or the clock-in). Allow asks once with getUserMedia / geolocation.
 * A browser can't open its own settings, so a blocked one gets a line saying where to allow it.
 * Device memory (not per person) is localStorage: whether the first sign-in screen was seen, and the reminder.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  afterSetup, devicePermissionRows, dismissReminder, EMPTY_REMINDER, missingPermissions, parseReminderState, permissionNeeds,
  type DevicePermission, type DevicePermissionStatus, type PermissionReminderState,
} from '@tradeoutlet/shared';
import { useMeToday } from '@tradeoutlet/data';
import { api } from './api';
import { useWorkspaceId } from './hooks';
import { holdsAnywhere } from './navAccess';
import { usePermissions } from './permissions';
import { enablePush, isPushOn, pushUiState, readPushEnv } from './push';

export type Statuses = Partial<Record<DevicePermission, DevicePermissionStatus>>;

/** A browser permission state as ours. Undefined (no Permissions API, or it doesn't know the name): unknown. */
export function browserStatus(s: PermissionState | undefined): DevicePermissionStatus {
  return s === 'granted' ? 'granted' : s === 'denied' ? 'denied' : s === 'prompt' ? 'ask' : 'unknown';
}

async function query(name: string): Promise<PermissionState | undefined> {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return undefined;
    return (await navigator.permissions.query({ name: name as PermissionName })).state;
  } catch { return undefined; }
}

const hasMedia = () => typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

/** iPhone Safari outside the Home Screen: push needs the Home Screen app first. */
export const notificationsNeedInstall = () => pushUiState(readPushEnv()) === 'ios-install';

async function readAll(pushReady: boolean): Promise<Statuses> {
  const env = readPushEnv();
  const ui = pushUiState(env);
  const notifications: DevicePermissionStatus = ui === 'ios-install' || ui === 'unsupported' ? 'unsupported'
    : ui === 'denied' ? 'denied'
    : env.permission === 'granted' && pushReady && (await isPushOn()) ? 'granted'
    : env.permission === 'granted' && !pushReady ? 'granted' : 'ask';
  const [mic, cam, loc] = await Promise.all([query('microphone'), query('camera'), query('geolocation')]);
  return {
    notifications,
    microphone: hasMedia() ? browserStatus(mic) : 'unsupported',
    camera: hasMedia() ? browserStatus(cam) : 'unsupported',
    location: typeof navigator !== 'undefined' && 'geolocation' in navigator ? browserStatus(loc) : 'unsupported',
  };
}

async function ask(kind: DevicePermission, publicKey: string | null | undefined): Promise<void> {
  if (kind === 'notifications') {
    if (publicKey) await enablePush(publicKey);
    else if ('Notification' in window) await Notification.requestPermission();
    return;
  }
  if (kind === 'location') {
    await new Promise<void>(res => navigator.geolocation.getCurrentPosition(() => res(), () => res(), { maximumAge: 60_000, timeout: 15_000 }));
    return;
  }
  // The browser's prompt; the stream is stopped at once (nothing is recorded).
  const stream = await navigator.mediaDevices.getUserMedia(kind === 'microphone' ? { audio: true } : { video: true });
  stream.getTracks().forEach(t => t.stop());
}

const changed = new Set<() => void>();
const announce = () => changed.forEach(f => f());

export function useDevicePermissions() {
  const ws = useWorkspaceId();
  const mine = usePermissions().data;
  const today = useMeToday(ws).data;
  const cfg = useQuery({ queryKey: ['push-config'], queryFn: () => api.pushConfig(), staleTime: 5 * 60_000 });
  const publicKey = cfg.data?.enabled ? cfg.data.public_key : null;
  const needs = permissionNeeds({ modulesOff: mine?.modules_off, geofence: today?.clock?.geofence, drives: holdsAnywhere(mine, 'fleet.drive') });
  // No Web Push on this server: the notifications row has nothing to turn on.
  const rows = useMemo(() => devicePermissionRows(needs).filter(r => r.kind !== 'notifications' || Boolean(publicKey) || cfg.isLoading),
    [needs.calls, needs.location, publicKey, cfg.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps
  const [status, setStatus] = useState<Statuses | null>(null);
  const [busy, setBusy] = useState<DevicePermission | null>(null);
  const refresh = useCallback(() => { void readAll(Boolean(publicKey)).then(setStatus); }, [publicKey]);
  useEffect(() => {
    refresh();
    changed.add(refresh);
    // Back from the browser's site settings.
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', refresh);
    return () => { changed.delete(refresh); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', refresh); };
  }, [refresh]);

  const request = useCallback(async (kind: DevicePermission) => {
    setBusy(kind);
    try { await ask(kind, publicKey); } catch { /* the row shows what the browser says */ } finally { setBusy(null); announce(); }
  }, [publicKey]);

  const ready = status !== null && !cfg.isLoading;
  const missing = ready ? missingPermissions(rows, status) : [];
  return { rows, status: status ?? {}, missing, ready, busy, request };
}

// ---- This device's memory (localStorage) ----
const SETUP_KEY = 'hub.permissions.setup-done';
const REMINDER_KEY = 'hub.permissions.reminder';
interface Prefs { setupDone: boolean; reminder: PermissionReminderState }
const read = (): Prefs => {
  try { return { setupDone: localStorage.getItem(SETUP_KEY) === '1', reminder: parseReminderState(localStorage.getItem(REMINDER_KEY)) }; } catch { return { setupDone: false, reminder: EMPTY_REMINDER }; }
};
const prefListeners = new Set<() => void>();
const write = (p: Prefs) => {
  try { localStorage.setItem(SETUP_KEY, p.setupDone ? '1' : '0'); localStorage.setItem(REMINDER_KEY, JSON.stringify(p.reminder)); } catch { /* private mode */ }
  prefListeners.forEach(l => l());
};

export function markSetupDone(now = Date.now()) { const p = read(); write({ setupDone: true, reminder: afterSetup(p.reminder, now) }); }
export function dismissPermissionReminder(now = Date.now()) { const p = read(); write({ ...p, reminder: dismissReminder(p.reminder, now) }); }

export function usePermissionPrefs(): Prefs {
  const [p, setP] = useState(read);
  useEffect(() => { const l = () => setP(read()); prefListeners.add(l); return () => { prefListeners.delete(l); }; }, []);
  return p;
}
